import { useEffect, useState } from "react";

import { controller } from "../controller";
import { OFFICE_FEATURES, featureOn } from "./sidebarAccess";

/**
 * The selected office's feature switches + banking state, cached.
 *
 * The sidebar remounts on every navigation (each page renders its own
 * DashboardLayout), so without a cache every page change would flash the menu
 * and every gated page would spin. So: memory first, then localStorage keyed by
 * office (logout already clears localStorage), one shared in-flight request, and
 * a background revalidation at most once a minute. Switching office reloads the
 * page, which simply reads the new office's entry.
 *
 * A failed or slow request leaves `failed: true`, which every consumer treats as
 * "today's behaviour" — never as locked.
 */
// v2: the payload gained the plan gate. Bumping the prefix means a cached v1
// entry (no `plan`) is simply ignored rather than read as "not gated".
const CACHE_PREFIX = "officeFeatures:v2:";
const REVALIDATE_MS = 60 * 1000;
const TIMEOUT_MS = 8 * 1000;
const MODES = ["default", "on", "off"];
export const OFFICE_FEATURES_EVENT = "office-features";

const memory = {}; // officeId -> { value, fetchedAt, failed, promise }

export function isUsableOfficeId(id) {
  if (id === null || id === undefined) return false;
  return /^\d+$/.test(String(id).trim());
}

export function currentOfficeId() {
  try {
    const id = localStorage.getItem("selectedOffice");
    return isUsableOfficeId(id) ? String(id).trim() : null;
  } catch (e) {
    return null;
  }
}

/** Whitelist the server payload into the shape the policy functions read. */
export function normalizeOfficeFeatures(json) {
  const raw = (json && typeof json.features === "object" && json.features) || {};
  const features = {};
  OFFICE_FEATURES.forEach(({ key }) => {
    features[key] = MODES.includes(raw[key]) ? raw[key] : "default";
  });
  const banking = json ? json.banking_configured : null;
  return {
    features,
    bankingConfigured: banking === true || banking === false ? banking : null,
    isSuperAdmin: !!(json && json.is_super_admin === true),
    plan: normalizePlanGate(json && json.plan),
  };
}

const PLAN_STATES = ["subscribed", "exempt", "trial", "open", "blocked"];

/** The office's plan gate for THIS viewer (``usage.services.access``), or null
 * when the backend didn't say — which every consumer reads as "not gated". */
export function normalizePlanGate(plan) {
  if (!plan || typeof plan !== "object" || !PLAN_STATES.includes(plan.state)) return null;
  const trial = plan.trial && typeof plan.trial === "object" ? plan.trial : null;
  return {
    state: plan.state,
    reason: plan.reason === "trial_exhausted" || plan.reason === "no_plan" ? plan.reason : null,
    gated: plan.gated === true,
    canPurchase: plan.can_purchase === true,
    trial: trial
      ? {
          active: !!trial.active,
          exhausted: !!trial.exhausted,
          used: Number(trial.used) || 0,
          quota: Number(trial.quota) || 0,
          remaining: Number(trial.remaining) || 0,
        }
      : null,
  };
}

export function readCachedOfficeFeatures(officeId) {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + officeId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.features ? parsed : null;
  } catch (e) {
    return null;
  }
}

function writeCachedOfficeFeatures(officeId, value) {
  try {
    localStorage.setItem(CACHE_PREFIX + officeId, JSON.stringify(value));
  } catch (e) {
    // Private mode / quota: the in-memory entry still works for this page.
  }
}

function broadcast(officeId) {
  try {
    window.dispatchEvent(new CustomEvent(OFFICE_FEATURES_EVENT, { detail: { officeId } }));
  } catch (e) {
    // Very old browsers: consumers still get the value from their own load().
  }
}

function withTimeout(promise) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), TIMEOUT_MS)),
  ]);
}

/** Load (or reuse) the office's status. Resolves to the value, or null on failure. */
export function loadOfficeFeatures(officeId, { force = false } = {}) {
  if (!isUsableOfficeId(officeId)) return Promise.resolve(null);
  const entry = memory[officeId] || {};
  if (entry.promise) return entry.promise;
  if (!force && entry.fetchedAt && Date.now() - entry.fetchedAt < REVALIDATE_MS) {
    return Promise.resolve(entry.value || null);
  }
  const promise = withTimeout(controller.getOfficeFeatureStatus(officeId))
    .then((res) => {
      if (!res || res.status !== 200 || !res.json) throw new Error("status " + (res && res.status));
      const value = normalizeOfficeFeatures(res.json);
      memory[officeId] = { value, fetchedAt: Date.now(), failed: false };
      writeCachedOfficeFeatures(officeId, value);
      return value;
    })
    .catch(() => {
      memory[officeId] = { value: entry.value || null, fetchedAt: Date.now(), failed: !entry.value };
      return entry.value || null;
    })
    .then((value) => {
      broadcast(officeId);
      return value;
    });
  memory[officeId] = { ...entry, promise };
  return promise;
}

function snapshot(officeId) {
  const entry = memory[officeId] || {};
  const value = entry.value || readCachedOfficeFeatures(officeId);
  if (value) return { ready: true, failed: false, plan: null, ...value };
  return { ready: !!entry.failed, failed: !!entry.failed, features: null, bankingConfigured: null,
           isSuperAdmin: false, plan: null };
}

/**
 * {ready, failed, features, bankingConfigured, isSuperAdmin} for the selected
 * office. Starts from the cache (so a returning visit renders immediately) and
 * revalidates in the background.
 */
export function useOfficeFeatures(officeId = currentOfficeId()) {
  const [state, setState] = useState(() => (officeId ? snapshot(officeId) : {
    ready: true, failed: true, features: null, bankingConfigured: null, isSuperAdmin: false, plan: null,
  }));

  useEffect(() => {
    if (!officeId) return undefined;
    let alive = true;
    const refresh = () => alive && setState(snapshot(officeId));
    const onEvent = (e) => {
      if (!e || !e.detail || String(e.detail.officeId) === String(officeId)) refresh();
    };
    window.addEventListener(OFFICE_FEATURES_EVENT, onEvent);
    loadOfficeFeatures(officeId).then(refresh);
    return () => {
      alive = false;
      window.removeEventListener(OFFICE_FEATURES_EVENT, onEvent);
    };
  }, [officeId]);

  return state;
}

/**
 * Synchronous best guess for event handlers (e.g. "Take the tour"): is
 * ``featureKey`` on for the selected office right now? Unknown = today's default.
 */
export function cachedFeatureOn(featureKey) {
  const officeId = currentOfficeId();
  const snap = officeId ? snapshot(officeId) : null;
  if (!snap || !snap.features) return featureOn(featureKey, {});
  return featureOn(featureKey, { isSuperAdmin: snap.isSuperAdmin, features: snap.features });
}

/** For tests only. */
export function __resetOfficeFeaturesMemory() {
  Object.keys(memory).forEach((k) => delete memory[k]);
}
